import { createContext, useContext, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CircleNotch, PaperPlaneRight, Megaphone, X } from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import MicButton from "@/components/MicButton";

const FeedbackContext = createContext(null);
export const useFeedback = () => useContext(FeedbackContext);

const PROMPT = "We'd love your feedback. Help us improve.";

export function FeedbackProvider({ children }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [hideBar, setHideBar] = useState(false);

  const openFeedback = useCallback(() => setOpen(true), []);

  const submit = async () => {
    const message = msg.trim();
    if (!message) { toast.error("Please add a short message first."); return; }
    setSending(true);
    try {
      await api.submitAppFeedback({ message, page: window.location.pathname, email: user?.email || null });
      toast.success("Thank you — your feedback was sent!");
      setMsg(""); setOpen(false);
    } catch (err) {
      toast.error("Could not send feedback", { description: err?.response?.data?.detail || err.message });
    } finally { setSending(false); }
  };

  return (
    <FeedbackContext.Provider value={{ openFeedback }}>
      {children}

      {!hideBar && (
        <div data-testid="feedback-bar" className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-center gap-3 border-t border-primary/30 bg-primary/10 px-4 py-1.5 backdrop-blur-sm">
          <span className="flex items-center gap-1.5 text-center text-[12px] font-medium text-foreground">
            <Megaphone size={14} weight="fill" className="hidden shrink-0 text-primary sm:block" />
            <span className="text-muted-foreground">This tool is still in active development.</span>
            <span className="hidden font-semibold text-foreground sm:inline">{PROMPT}</span>
          </span>
          <button data-testid="feedback-bar-btn" onClick={openFeedback} className="shrink-0 rounded-full bg-primary px-3 py-1 text-[12px] font-semibold text-primary-foreground transition-opacity hover:opacity-90">
            Share feedback
          </button>
          <button aria-label="Hide" onClick={() => setHideBar(true)} className="shrink-0 text-muted-foreground hover:text-foreground"><X size={14} /></button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="feedback-dialog" className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{PROMPT}</DialogTitle>
            <DialogDescription>
              This tool is still in development and your input directly shapes it. Tell us what worked, what confused you, or what you'd love to see — anything helps.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Textarea data-testid="feedback-textarea" value={msg} onChange={(e) => setMsg(e.target.value)} rows={5}
              placeholder="Your thoughts, ideas, or anything that got in your way…" className="resize-none pr-11" />
            <MicButton size={17} className="absolute right-2.5 top-2.5" onResult={(t) => setMsg((p) => (p ? p.trim() + " " + t : t))} />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="rounded-sm border border-border px-3 py-2 text-sm hover:bg-muted">Cancel</button>
            <button data-testid="feedback-submit" onClick={submit} disabled={sending || !msg.trim()} className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">
              {sending ? <CircleNotch size={15} className="animate-spin" /> : <PaperPlaneRight size={15} />} Send feedback
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </FeedbackContext.Provider>
  );
}
